import { A11yModule } from '@angular/cdk/a11y';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  input,
  Input,
  Output,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DirtyInvalidErrorStateMatcher } from '@examdojo/angular/forms';
import { connectState, InputObservable } from '@examdojo/angular/util';
import { CopyToClipboardComponent } from '@examdojo/core/copy-to-clipboard';
import { ErrorMessages, FormFieldLabelDirective, selectFirstErrorMessage } from '@examdojo/core/form';
import { InfoIconComponent } from '@examdojo/core/info-icon';
import { ComponentSize, DEFAULT_COMPONENT_SIZE } from '@examdojo/core/model';
import { ReactiveLifecycleHooksDirective } from '@examdojo/core/reactive-lifecycle-hooks';
import { fromResize } from '@examdojo/core/rxjs';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, delay, filter, map, NEVER, Observable, Subject, switchMap, tap } from 'rxjs';

const INPUT_IMPORTS: Component['imports'] = [
  ReactiveFormsModule,
  MatFormFieldModule,
  MatInputModule,
  MatTooltipModule,
  InfoIconComponent,
  CopyToClipboardComponent,
  FormFieldLabelDirective,
  A11yModule,
  NgIf,
  NgTemplateOutlet,
  NgClass,
  CdkTextareaAutosize,
];

@UntilDestroy()
@Directive()
export class _InputComponent<T = unknown> extends ReactiveLifecycleHooksDirective {
  constructor() {
    super();

    this.ngAfterViewInit$
      .pipe(
        delay(0),
        filter(() => !!this.hasInitialFocus),
        tap(() => this.matInput()?.focus()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }
  readonly matInput = viewChild<MatInput>(MatInput);

  @Input()
  @InputObservable()
  label?: string | TemplateRef<unknown> | null;
  private readonly label$!: Observable<string | TemplateRef<unknown> | null>;

  @Input({ required: true }) formCtrl!: FormControl<T | null | undefined>;
  @Input() type: 'text' | 'password' | 'number' | 'textarea' | 'email' = 'text';
  @Input() hideErrors = false;
  @Input() errorMessages?: ErrorMessages;
  @Input() required = false;
  @Input() color?: MatFormField['color'];
  @Input() tooltipHint?: string | null;
  @Input() enableCopyToClipboard = false;
  @Input() compact?: boolean | null;
  @Input() upperCase?: boolean | null;
  @Input() hasInitialFocus?: boolean;
  @Input() fullWidthField = false;
  @Input() resizable = true;
  @Input() inputmode?: string;
  readonly autoResize = input(false);
  @Input() autoResizeMaxRows?: number;
  @Input() autoResizeMinRows?: number;
  readonly size = input<ComponentSize>(DEFAULT_COMPONENT_SIZE);

  /* number input only */
  @Input() step = 1;
  @Input() min: number | null = null;
  @Input() max: number | null = null;

  // HTMLInputElement properties
  @Input() placeholder?: HTMLInputElement['placeholder'];
  @Input() autocomplete?: HTMLInputElement['autocomplete'];
  @Input() readOnly?: HTMLInputElement['readOnly'];
  @Input() tabIndex?: HTMLInputElement['tabIndex'];
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() input = new EventEmitter<Event>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() keyup = new EventEmitter<KeyboardEvent>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur = new EventEmitter<FocusEvent>();
  // ---

  @Input() errorStateMatcher = new DirtyInvalidErrorStateMatcher();

  private readonly tooltip$$ = new BehaviorSubject('');

  readonly state = connectState({
    labelTemplate: this.label$.pipe(
      map((label) => label instanceof TemplateRef),
      map((isTemplatRef) => (isTemplatRef ? (this.label as TemplateRef<unknown>) : null)),
    ),
    errorMessage: this.ngAfterViewInit$.pipe(
      map(() => this.formCtrl),
      switchMap((ctrl) => selectFirstErrorMessage(ctrl, this.errorMessages)),
    ),
    tooltip: this.tooltip$$,
  });

  setTooltip(message: string) {
    this.tooltip$$.next(message);
  }

  focus() {
    this.matInput()?.focus();
  }
}

@Component({
  selector: 'dojo-text-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  imports: [INPUT_IMPORTS],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextInputComponent extends _InputComponent<string> {
  @Input() override fullWidthField = false;
  @Input() override type: _InputComponent['type'] = 'text';
}

@Component({
  selector: 'dojo-number-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  imports: [INPUT_IMPORTS],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberInputComponent extends _InputComponent<number> {
  @Input() override fullWidthField = false;
  override readonly type: _InputComponent['type'] = 'number';
}
@Component({
  selector: 'dojo-textarea-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  imports: [INPUT_IMPORTS],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextareaInputComponent extends _InputComponent<string> {
  constructor() {
    super();

    // By default the auto-resize behavior is not triggered when the width of the textarea is changed.
    // It's especially visible during animations like the as-split resize when it comes from a 0px width where a placeholder can take maaaany lines.
    combineLatest([toObservable(this.autoResize), toObservable(this.matInput)])
      .pipe(
        switchMap(([autoResize, matInput]) => {
          if (!autoResize || !matInput) {
            return NEVER;
          }

          return fromResize((matInput as unknown as { _elementRef: ElementRef })?._elementRef.nativeElement);
        }),
        tap(() => {
          const textareaAutosize = this.textareaAutosize();
          (textareaAutosize as unknown as { _resizeEvents: Subject<void> })?._resizeEvents?.next();
        }),
      )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }
  @Input() override fullWidthField = false;
  override readonly type: _InputComponent['type'] = 'textarea';

  private readonly textareaAutosize = viewChild<CdkTextareaAutosize>(CdkTextareaAutosize);
}
